import { type RefObject, useCallback, useEffect, useRef } from "react";

/**
 * Keeps a canvas at device-pixel resolution (its CSS box sets the size) and runs
 * `draw` on animation frames for as long as it returns true. Idle boards cost
 * nothing; call the returned `kick` whenever something new should animate.
 */
export function useCanvasLoop(
  ref: RefObject<HTMLCanvasElement | null>,
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number, now: number) => boolean,
): () => void {
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const raf = useRef(0);

  const frame = useCallback(
    (now: number) => {
      raf.current = 0;
      const c = ref.current;
      const ctx = c?.getContext("2d");
      if (!c || !ctx || c.width === 0) return;
      if (drawRef.current(ctx, c.width, c.height, now)) raf.current = requestAnimationFrame(frame);
    },
    [ref],
  );

  const kick = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(frame);
  }, [frame]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return undefined;
    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = Math.round(c.clientWidth * dpr);
      const h = Math.round(c.clientHeight * dpr);
      if (w !== c.width || h !== c.height) {
        c.width = w;
        c.height = h;
      }
      kick();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(c);
    return () => {
      ro.disconnect();
      if (raf.current) cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [ref, kick]);

  return kick;
}
